import React, { useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
// Import from 'react-native' for maximum code reuse - Vite aliases this to 'react-native-web'
import { Animated, Platform } from 'react-native';
// Use hybrid components to support className prop for CSS styling
import { View, Text } from './rn-web-hybrid';
import { HeroLogo } from './HeroLogo';
import { archetypeLine } from '../../shared/src/results/catalog';
import { myersBriggsTypes } from '../../shared/src/results/archetypeSets';
import { NavBarLogo } from './NavBarLogo';
import RealtimePage from './RealtimePage';
import { OpenAppLink } from './SignIn';
import AdminPage from './AdminPage';
import Svg, {
  Path,
  Circle,
  Rect,
  Ellipse,
  Defs,
  LinearGradient,
  RadialGradient,
  Stop,
} from 'react-native-svg';

// Create animated SVG components
const AnimatedCircle = Animated.createAnimatedComponent(Circle);

// Platform-specific animated circle component
interface PulsingCircleProps {
  cx: number;
  cy: number;
  minR?: number;
  maxR?: number;
  duration?: number;
  delay?: number;
  fill: string;
  initialOpacity?: number;
}

const PulsingCircle = ({
  cx,
  cy,
  minR = 2,
  maxR = 3,
  duration = 2000,
  delay = 0,
  fill,
  initialOpacity = 0.6,
  ...props
}: PulsingCircleProps) => {
  const isWeb = Platform.OS === 'web';
  const animatedRadius = useRef(new Animated.Value(minR)).current;
  const animatedOpacity = useRef(new Animated.Value(initialOpacity)).current;

  useEffect(() => {
    // Only use Animated API on native platforms
    if (!isWeb) {
      // Radius animation
      const radiusAnimation = Animated.loop(
        Animated.sequence([
          Animated.timing(animatedRadius, {
            toValue: maxR,
            duration: duration / 2,
            useNativeDriver: false,
          }),
          Animated.timing(animatedRadius, {
            toValue: minR,
            duration: duration / 2,
            useNativeDriver: false,
          }),
        ])
      );

      // Opacity animation
      const opacityAnimation = Animated.loop(
        Animated.sequence([
          Animated.timing(animatedOpacity, {
            toValue: 1,
            duration: duration / 2,
            useNativeDriver: false,
          }),
          Animated.timing(animatedOpacity, {
            toValue: initialOpacity,
            duration: duration / 2,
            useNativeDriver: false,
          }),
        ])
      );

      // Start animations with delay
      const timer = setTimeout(() => {
        radiusAnimation.start();
        opacityAnimation.start();
      }, delay);

      return () => {
        clearTimeout(timer);
        radiusAnimation.stop();
        opacityAnimation.stop();
      };
    }
  }, [isWeb, animatedRadius, animatedOpacity, minR, maxR, duration, delay, initialOpacity]);

  // For web, use a simple Circle without animation for React Native SVG
  if (isWeb) {
    // Since react-native-svg doesn't support animate children, just return a static circle
    // The animations can be handled via CSS if needed
    return <Circle cx={cx} cy={cy} r={minR} fill={fill} opacity={initialOpacity} />;
  }

  // For native platforms, use React Native Animated API
  return (
    <AnimatedCircle
      cx={cx}
      cy={cy}
      r={animatedRadius}
      fill={fill}
      opacity={animatedOpacity}
      {...props}
    />
  );
};

// OS Logo Components using react-native-svg
const AppleLogo = () => (
  <Svg
    style={{ transform: 'translateY(-1px)' }}
    viewBox="0 0 24 24"
    fill="currentColor"
    width="24"
    height="24"
  >
    <Path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </Svg>
);

export const AndroidLogo = () => (
  <Svg viewBox="0 0 24 24" fill="currentColor" width={24} height={24}>
    <Path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-2.86-1.21-6.08-1.21-8.94 0L5.65 5.67c-.19-.29-.51-.38-.83-.22-.31.16-.43.53-.27.85L6.4 9.48C3.3 11.25 1.28 14.44 1 18h22c-.28-3.56-2.3-6.75-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z" />
  </Svg>
);

export const WindowsLogo = () => (
  <Svg viewBox="0 0 24 24" fill="currentColor" width={24} height={24}>
    <Path d="M3 5.5L10.5 4.5V11.5H3V5.5ZM10.5 12.5V19.5L3 18.5V12.5H10.5ZM11.5 4.35L21 3V11.5H11.5V4.35ZM21 12.5V21L11.5 19.65V12.5H21Z" />
  </Svg>
);

export const LinuxLogo = () => (
  <Svg viewBox="0 0 24 24" fill="currentColor" width={24} height={24}>
    <Path d="M12 5C10.89 5 10 5.89 10 7V9.27C9.38 9.63 9 10.29 9 11V14C9 14.55 9.45 15 10 15H11V19C11 19.55 11.45 20 12 20S13 19.55 13 19V15H14C14.55 15 15 14.55 15 14V11C15 10.29 14.62 9.63 14 9.27V7C14 5.89 13.11 5 12 5M11 7C11 6.45 11.45 6 12 6S13 6.45 13 7V9H11V7Z" />
  </Svg>
);

const DownloadPage = () => {
  const [release, setRelease] = React.useState<{
    version: string;
    signed: boolean;
    downloadUrl: string;
  } | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/desktop-release/arm64', { signal: controller.signal })
      .then(async (response) => {
        if (response.ok) setRelease(await response.json());
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoaded(true);
      });
    return () => controller.abort();
  }, []);
  return (
    <div className="legal-page">
      <Link to="/" className="back-to-home">
        ← SoulThread
      </Link>
      <div className="legal-content">
        <h1>Your conversations, connected.</h1>
        <p>
          The Mac app keeps iMessage, WhatsApp, Signal, and Telegram in sync on its own. Browse your
          conversations and ask about yourself from the web app, too.
        </p>
        {release ? (
          <>
            <a className="btn btn-primary btn-large" href={release.downloadUrl}>
              <AppleLogo />
              Download for Apple silicon · {release.version}
            </a>
            <p>Open the disk image and drag SoulThread into Applications.</p>
            {!release.signed && (
              <p>
                This preview uses manual updates. If macOS blocks the first launch, open System
                Settings → Privacy &amp; Security and choose Open Anyway for SoulThread.
              </p>
            )}
          </>
        ) : (
          <p>
            {loaded
              ? 'The next desktop release is being prepared. You can use the web app while it becomes available.'
              : 'Checking the latest release…'}
          </p>
        )}
        <p>
          <OpenAppLink>Open the web app ↗</OpenAppLink>
        </p>
      </div>
    </div>
  );
};

const WaitlistForm = ({ compact = false }: { compact?: boolean }) => {
  const [email, setEmail] = React.useState('');
  const [status, setStatus] = React.useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = React.useState('');
  const [website, setWebsite] = React.useState('');

  const submitWaitlistEmail = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('submitting');
    setMessage('');

    try {
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim(), website }),
      });

      const data = (await response.json().catch(() => null)) as {
        message?: string;
        error?: string;
      } | null;

      if (!response.ok) {
        throw new Error(data?.error || 'Unable to join waitlist');
      }

      setStatus('success');
      setMessage(data?.message || "You're on the waitlist.");
      setEmail('');
      setWebsite('');
    } catch (error) {
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Unable to join waitlist');
    }
  };

  const formClassName = compact ? 'waitlist-form waitlist-form-compact' : 'waitlist-form';

  return (
    <form className={formClassName} onSubmit={submitWaitlistEmail}>
      <input
        type="email"
        className="waitlist-input"
        placeholder="Enter your email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        required
        autoComplete="email"
        aria-label="Email address"
      />
      <input
        type="text"
        name="website"
        className="waitlist-honeypot"
        value={website}
        onChange={(event) => setWebsite(event.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />
      <button
        type="submit"
        className="btn btn-primary waitlist-submit"
        disabled={status === 'submitting'}
      >
        {status === 'submitting' ? 'Joining...' : 'Join Waitlist'}
      </button>
      <Text className="waitlist-disclaimer">We’ll only email you about SoulThread updates.</Text>
      {message && <Text className={`waitlist-message waitlist-message-${status}`}>{message}</Text>}
    </form>
  );
};

// Hook to get responsive logo size
const useResponsiveLogoSize = () => {
  const [logoSize, setLogoSize] = React.useState(300);

  React.useEffect(() => {
    const updateSize = () => {
      const width = window.innerWidth;
      if (width <= 360) {
        setLogoSize(Math.min(168, width - 116));
      } else if (width <= 480) {
        setLogoSize(Math.min(184, width - 124));
      } else if (width <= 768) {
        setLogoSize(Math.min(248, width - 116));
      } else {
        setLogoSize(300);
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  return logoSize;
};

// Scroll-triggered quote reveal component
interface ScrollQuoteProps {
  words: string[];
  attribution: string;
}

const ScrollQuote: React.FC<ScrollQuoteProps> = ({ words, attribution }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleWords, setVisibleWords] = React.useState(0);
  const visibleWordsRef = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementTop = rect.top;

      // Calculate progress: 0 when element enters viewport, 1 when it reaches center
      const scrollProgress = Math.max(
        0,
        Math.min(1, (windowHeight - elementTop) / (windowHeight * 0.7))
      );

      // Determine how many words should be visible based on scroll progress
      const targetWords = Math.floor(scrollProgress * (words.length + 1));

      if (targetWords > visibleWordsRef.current) {
        visibleWordsRef.current = targetWords;
        setVisibleWords(targetWords);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [words.length]);

  // Use native HTML elements with CSS classes for gradient text,
  // since React Native Web strips backgroundClip/WebkitTextFillColor from inline styles
  return (
    <div ref={containerRef} className="scroll-quote-container">
      <div className="scroll-quote-words">
        {words.map((word, index) => (
          <span
            key={index}
            className={`scroll-quote-word${index < visibleWords ? ' visible' : ''}`}
          >
            {word}
          </span>
        ))}
      </div>
      <span className={`scroll-quote-attribution${visibleWords > words.length ? ' visible' : ''}`}>
        {attribution}
      </span>
    </div>
  );
};

// Hook to observe elements and add 'visible' class when scrolled into view
const useScrollReveal = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const observedSetRef = useRef<Set<Element>>(new Set());

  const observe = useCallback((el: HTMLElement | null) => {
    if (!el || !observerRef.current || observedSetRef.current.has(el)) return;
    observedSetRef.current.add(el);
    observerRef.current.observe(el);
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('scroll-visible');
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    // Fallback for components that don't forward refs consistently.
    const elements = Array.from(document.querySelectorAll('.scroll-reveal'));
    elements.forEach((el) => observe(el as HTMLElement));

    const observed = observedSetRef.current;
    return () => {
      observerRef.current?.disconnect();
      observed.clear();
    };
  }, [observe]);

  return observe;
};

// Page transition wrapper
const PageTransition = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  return (
    <div key={location.pathname} className="page-transition">
      {children}
    </div>
  );
};

// Marketing/Landing Page
const HomePage = ({ waitlistMode }: { waitlistMode: boolean }) => {
  const logoSize = useResponsiveLogoSize();
  const observe = useScrollReveal();

  return (
    <View className="home-page">
      <View className="hero-section">
        <View className="hero-content">
          <View style={{ marginBottom: '2rem', flexDirection: 'row', justifyContent: 'center' }}>
            <HeroLogo size={logoSize} className="hero-logo" />
          </View>
          <Text className="hero-subtitle" style={{ display: 'block' }}>
            A picture is worth a thousand words.{' '}
            <span className="hero-subtitle-turn">You’ve already written yours.</span>
          </Text>

          <View className="hero-cta">
            <OpenAppLink className="btn btn-primary btn-large web-app-hero-link">
              Open SoulThread <span aria-hidden="true">↗</span>
            </OpenAppLink>
            {waitlistMode && (
              <View id="waitlist">
                <WaitlistForm />
              </View>
            )}
          </View>
        </View>
      </View>

      {/* Aristotle Quote Reveal */}
      <ScrollQuote words={['All', 'wisdom', 'begins', 'within.']} attribution="— Aristotle" />

      <View className="features-section">
        <View>
          <Text
            className="section-title features-title"
            accessibilityRole="header"
            accessibilityLevel={2}
          >
            Get To Know the <Text className="title-break">Real You</Text>
          </Text>
          <Text className="section-subtitle">See yourself the way your messages already do</Text>
        </View>
        <View className="features-grid">
          <View
            className="feature-card premium scroll-reveal"
            ref={(el: HTMLElement | null) => observe(el)}
          >
            <View className="feature-icon-wrapper">
              <Svg viewBox="0 0 48 48" fill="none">
                <Defs>
                  <LinearGradient id="brain-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <Stop offset="0%" stopColor="#67e8f9" />
                    <Stop offset="50%" stopColor="#a78bfa" />
                    <Stop offset="100%" stopColor="#f9a8d4" />
                  </LinearGradient>
                  <LinearGradient id="brain-fill" x1="0%" y1="0%" x2="100%" y2="100%">
                    <Stop offset="0%" stopColor="#67e8f9" stopOpacity="0.15" />
                    <Stop offset="100%" stopColor="#a78bfa" stopOpacity="0.15" />
                  </LinearGradient>
                </Defs>
                <Circle cx="24" cy="24" r="20" fill="url(#brain-fill)" />
                <Circle cx="24" cy="24" r="6" fill="url(#brain-fill)" />
                <Circle
                  cx="24"
                  cy="24"
                  r="6"
                  stroke="url(#brain-gradient)"
                  strokeWidth="2"
                  fill="none"
                />
                <Circle cx="12" cy="16" r="3" fill="url(#brain-fill)" />
                <Circle
                  cx="12"
                  cy="16"
                  r="3"
                  stroke="url(#brain-gradient)"
                  strokeWidth="1.5"
                  fill="none"
                />
                <Circle cx="36" cy="16" r="3" fill="url(#brain-fill)" />
                <Circle
                  cx="36"
                  cy="16"
                  r="3"
                  stroke="url(#brain-gradient)"
                  strokeWidth="1.5"
                  fill="none"
                />
                <Circle cx="12" cy="32" r="3" fill="url(#brain-fill)" />
                <Circle
                  cx="12"
                  cy="32"
                  r="3"
                  stroke="url(#brain-gradient)"
                  strokeWidth="1.5"
                  fill="none"
                />
                <Circle cx="36" cy="32" r="3" fill="url(#brain-fill)" />
                <Circle
                  cx="36"
                  cy="32"
                  r="3"
                  stroke="url(#brain-gradient)"
                  strokeWidth="1.5"
                  fill="none"
                />
                <Path
                  d="M18 24L15 16M30 24L33 16M18 24L15 32M30 24L33 32"
                  stroke="url(#brain-gradient)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  opacity="0.7"
                />
                {/* Animated circle using React Native Animated API */}
                <PulsingCircle
                  cx={24}
                  cy={24}
                  minR={2}
                  maxR={3}
                  duration={2000}
                  fill="url(#brain-gradient)"
                  initialOpacity={0.5}
                />
              </Svg>
            </View>
            <View>
              <Text className="feature-title" accessibilityRole="header" accessibilityLevel={3}>
                Reads Between the Lines
              </Text>
              <Text className="feature-description">
                Finds the patterns in how you talk, who you talk to, and how that changes over time
              </Text>
            </View>
            <View className="feature-badge">Your Real Words</View>
          </View>
          <View
            className="feature-card premium scroll-reveal"
            ref={(el: HTMLElement | null) => observe(el)}
          >
            <View className="feature-icon-wrapper">
              <Svg viewBox="0 0 48 48" fill="none">
                <Defs>
                  <LinearGradient id="lock-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <Stop offset="0%" stopColor="#67e8f9" />
                    <Stop offset="100%" stopColor="#f9a8d4" />
                  </LinearGradient>
                  <LinearGradient id="lock-fill" x1="0%" y1="0%" x2="100%" y2="100%">
                    <Stop offset="0%" stopColor="#67e8f9" stopOpacity="0.15" />
                    <Stop offset="100%" stopColor="#f9a8d4" stopOpacity="0.15" />
                  </LinearGradient>
                </Defs>
                <Circle cx="24" cy="24" r="20" fill="url(#lock-fill)" />
                <Path
                  d="M24 10L35 15V25C35 31.5 30.5 36 24 40C17.5 36 13 31.5 13 25V15L24 10Z"
                  fill="rgba(103, 232, 249, 0.1)"
                />
                <Path
                  d="M24 10L35 15V25C35 31.5 30.5 36 24 40C17.5 36 13 31.5 13 25V15L24 10Z"
                  stroke="url(#lock-gradient)"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                  fill="none"
                />
                <Rect
                  x="19"
                  y="22.5"
                  width="10"
                  height="8"
                  rx="1.5"
                  fill="rgba(103, 232, 249, 0.15)"
                />
                <Rect
                  x="19"
                  y="22.5"
                  width="10"
                  height="8"
                  rx="1.5"
                  stroke="url(#lock-gradient)"
                  strokeWidth="1.3"
                  fill="none"
                />
                <Path
                  d="M21.5 22.5V20C21.5 18.6 22.6 17.5 24 17.5C25.4 17.5 26.5 18.6 26.5 20V22.5"
                  stroke="url(#lock-gradient)"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  fill="none"
                />
                <Circle cx="24" cy="26" r="1.2" fill="url(#lock-gradient)" />
                <Path
                  d="M24 27L24 28.5"
                  stroke="url(#lock-gradient)"
                  strokeWidth="1.1"
                  strokeLinecap="round"
                />
              </Svg>
            </View>
            <View>
              <Text className="feature-title" accessibilityRole="header" accessibilityLevel={3}>
                Private by Design
              </Text>
              <Text className="feature-description">
                Your results are shown only to you, and we never sell your data. Disconnect any app
                anytime, or ask us to delete it.
              </Text>
            </View>
            <View className="feature-badge">Yours Alone</View>
          </View>
          <View
            className="feature-card premium scroll-reveal"
            ref={(el: HTMLElement | null) => observe(el)}
          >
            <View className="feature-icon-wrapper">
              <Svg viewBox="0 0 48 48" fill="none">
                <Defs>
                  <RadialGradient id="personality-radial" cx="50%" cy="50%" r="50%">
                    <Stop offset="0%" stopColor="#f9a8d4" stopOpacity="0.3" />
                    <Stop offset="50%" stopColor="#a78bfa" stopOpacity="0.2" />
                    <Stop offset="100%" stopColor="#67e8f9" stopOpacity="0.1" />
                  </RadialGradient>
                  <LinearGradient id="personality-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <Stop offset="0%" stopColor="#f9a8d4" />
                    <Stop offset="100%" stopColor="#a78bfa" />
                  </LinearGradient>
                </Defs>
                <Circle cx="24" cy="24" r="20" fill="url(#personality-radial)" />
                <Ellipse
                  cx="24"
                  cy="24"
                  rx="18"
                  ry="8"
                  stroke="url(#personality-gradient)"
                  strokeWidth="1.5"
                  fill="none"
                  opacity="0.5"
                  transform="rotate(30 24 24)"
                />
                <Ellipse
                  cx="24"
                  cy="24"
                  rx="18"
                  ry="8"
                  stroke="url(#personality-gradient)"
                  strokeWidth="1.5"
                  fill="none"
                  opacity="0.5"
                  transform="rotate(-30 24 24)"
                />
                <Ellipse
                  cx="24"
                  cy="24"
                  rx="18"
                  ry="8"
                  stroke="url(#personality-gradient)"
                  strokeWidth="1.5"
                  fill="none"
                  opacity="0.5"
                  transform="rotate(90 24 24)"
                />
                <Circle cx="24" cy="24" r="6" fill="url(#personality-gradient)" opacity="0.3" />
                <Circle cx="24" cy="24" r="4" fill="url(#personality-gradient)" opacity="0.6" />
                <PulsingCircle
                  cx={24}
                  cy={24}
                  minR={2}
                  maxR={3}
                  duration={3000}
                  fill="url(#personality-gradient)"
                  initialOpacity={0.7}
                />
                <Circle cx="24" cy="24" r="4" fill="url(#personality-gradient)" opacity="0.8" />
                {/* Cleaner trait indicators in cardinal and ordinal directions */}
                <Circle cx="24" cy="8" r="1.5" fill="url(#personality-gradient)" opacity="0.9" />
                <Circle
                  cx="34.5"
                  cy="13.5"
                  r="1.5"
                  fill="url(#personality-gradient)"
                  opacity="0.9"
                />
                <Circle cx="40" cy="24" r="1.5" fill="url(#personality-gradient)" opacity="0.9" />
                <Circle
                  cx="34.5"
                  cy="34.5"
                  r="1.5"
                  fill="url(#personality-gradient)"
                  opacity="0.9"
                />
                <Circle cx="24" cy="40" r="1.5" fill="url(#personality-gradient)" opacity="0.9" />
                <Circle
                  cx="13.5"
                  cy="34.5"
                  r="1.5"
                  fill="url(#personality-gradient)"
                  opacity="0.9"
                />
                <Circle cx="8" cy="24" r="1.5" fill="url(#personality-gradient)" opacity="0.9" />
                <Circle
                  cx="13.5"
                  cy="13.5"
                  r="1.5"
                  fill="url(#personality-gradient)"
                  opacity="0.9"
                />
                {/* Subtle connecting lines from center to key traits */}
                <Path
                  d="M24 20L24 12M28 28L32 32M28 20L32 16M20 28L16 32M20 20L16 16M24 28L24 36M28 24L36 24M20 24L12 24"
                  stroke="url(#personality-gradient)"
                  strokeWidth="1"
                  opacity="0.3"
                />
              </Svg>
            </View>
            <View>
              <Text className="feature-title" accessibilityRole="header" accessibilityLevel={3}>
                100+ Personality Tests
              </Text>
              <Text className="feature-description">
                Complete coverage from MBTI and Big 5 to Enneagram, Love Languages, and viral
                quizzes
              </Text>
            </View>
            <View className="feature-badge">Most Comprehensive</View>
          </View>
        </View>
      </View>

      <View className="how-it-works">
        <View>
          <Text className="section-title" accessibilityRole="header" accessibilityLevel={2}>
            How It Works
          </Text>
          <Text className="section-subtitle">
            Three simple steps to discover your authentic self
          </Text>
        </View>

        <View
          className="steps how-it-works-grid"
          style={{
            alignItems: 'center',
          }}
        >
          <View
            className="step-card scroll-reveal"
            ref={(el: HTMLElement | null) => observe(el)}
            style={{
              background:
                'linear-gradient(135deg, rgba(26, 15, 46, 0.5) 0%, rgba(30, 20, 50, 0.6) 100%)',
              borderRadius: '1.5rem',
              padding: '2rem',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(103, 232, 249, 0.2)',
              position: 'relative',
              textAlign: 'center',
              flexGrow: 1,
              maxWidth: 365,
            }}
          >
            <View
              className="step-number"
              style={{
                width: 60,
                height: 60,
                borderRadius: 30,
                background: 'linear-gradient(135deg, #67e8f9 0%, #a78bfa 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: 'auto',
                marginRight: 'auto',
                marginBottom: 24,
                fontSize: 24,
                fontWeight: 'bold',
                color: '#1a0f2e',
              }}
            >
              1
            </View>

            <View
              className="step-icon-container"
              style={{
                width: 140,
                height: 140,
                marginLeft: 'auto',
                marginRight: 'auto',
                marginBottom: 24,
                padding: 16,
                backgroundColor: 'rgba(5, 150, 105, 0.1)',
                borderRadius: 16,
                borderWidth: 1,
                borderColor: 'rgba(5, 150, 105, 0.3)',
                borderStyle: 'solid',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Svg viewBox="0 0 48 48" fill="none" style={{ width: '100%', height: '100%' }}>
                <Defs>
                  <LinearGradient id="download-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <Stop offset="0%" stopColor="#67e8f9" />
                    <Stop offset="100%" stopColor="#059669" />
                  </LinearGradient>
                  <LinearGradient id="download-fill" x1="0%" y1="0%" x2="100%" y2="100%">
                    <Stop offset="0%" stopColor="#67e8f9" stopOpacity="0.1" />
                    <Stop offset="100%" stopColor="#059669" stopOpacity="0.1" />
                  </LinearGradient>
                </Defs>
                <Rect x="12" y="8" width="24" height="32" rx="4" fill="url(#download-fill)" />
                <Rect
                  x="12"
                  y="8"
                  width="24"
                  height="32"
                  rx="4"
                  stroke="url(#download-gradient)"
                  strokeWidth="2"
                  fill="none"
                />
                <Path
                  d="M18.5 23L22.5 27L29.5 19"
                  stroke="url(#download-gradient)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <Circle cx="24" cy="35" r="2" fill="url(#download-gradient)" />
              </Svg>
            </View>

            <Text
              accessibilityRole="header"
              accessibilityLevel={3}
              style={{
                fontSize: '20.8px',
                fontWeight: '600',
                marginBottom: 12,
                marginTop: 0,
                color: '#e2e8f0',
                lineHeight: '1.6',
                display: 'block',
                textAlign: 'center',
              }}
            >
              Open SoulThread
            </Text>
            <Text
              style={{
                fontSize: '15.2px',
                color: '#94a3b8',
                lineHeight: '1.5',
                margin: 0,
                display: 'block',
                textAlign: 'center',
              }}
            >
              Sign in on the web{'\n'}
              <Text style={{ opacity: 0.8 }}>by linking a messenger from your phone</Text>
            </Text>
          </View>

          <View
            className="step-card scroll-reveal"
            ref={(el: HTMLElement | null) => observe(el)}
            style={{
              background:
                'linear-gradient(135deg, rgba(26, 15, 46, 0.5) 0%, rgba(30, 20, 50, 0.6) 100%)',
              borderRadius: '1.5rem',
              padding: '2rem',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(249, 168, 212, 0.2)',
              position: 'relative',
              textAlign: 'center',
              flexGrow: 1,
              maxWidth: 365,
            }}
          >
            <View
              className="step-number"
              style={{
                width: 60,
                height: 60,
                borderRadius: 30,
                background: 'linear-gradient(135deg, #f9a8d4 0%, #67e8f9 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: 'auto',
                marginRight: 'auto',
                marginBottom: 24,
                fontSize: 24,
                fontWeight: 'bold',
                color: '#1a0f2e',
              }}
            >
              2
            </View>

            <View
              className="step-icon-container"
              style={{
                width: 140,
                height: 140,
                marginLeft: 'auto',
                marginRight: 'auto',
                marginBottom: 24,
                padding: 16,
                backgroundColor: 'rgba(103, 232, 249, 0.1)',
                borderRadius: 16,
                borderWidth: 1,
                borderColor: 'rgba(103, 232, 249, 0.3)',
                borderStyle: 'solid',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Svg viewBox="0 0 48 48" fill="none" style={{ width: '100%', height: '100%' }}>
                <Defs>
                  <LinearGradient id="chat-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <Stop offset="0%" stopColor="#67e8f9" />
                    <Stop offset="100%" stopColor="#a78bfa" />
                  </LinearGradient>
                  <LinearGradient id="chat-fill" x1="0%" y1="0%" x2="100%" y2="100%">
                    <Stop offset="0%" stopColor="#67e8f9" stopOpacity="0.15" />
                    <Stop offset="100%" stopColor="#a78bfa" stopOpacity="0.15" />
                  </LinearGradient>
                </Defs>
                {/* Left chat bubble - cleaner rounded rect with tail */}
                <Path
                  d="M6 16C6 14.3 7.3 13 9 13H21C22.7 13 24 14.3 24 16V22C24 23.7 22.7 25 21 25H12L6 29V16Z"
                  fill="url(#chat-fill)"
                />
                <Path
                  d="M6 16C6 14.3 7.3 13 9 13H21C22.7 13 24 14.3 24 16V22C24 23.7 22.7 25 21 25H12L6 29V16Z"
                  stroke="url(#chat-gradient)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
                {/* Right chat bubble - cleaner rounded rect with tail */}
                <Path
                  d="M42 20C42 18.3 40.7 17 39 17H27C25.3 17 24 18.3 24 20V26C24 27.7 25.3 29 27 29H36L42 33V20Z"
                  fill="url(#chat-fill)"
                />
                <Path
                  d="M42 20C42 18.3 40.7 17 39 17H27C25.3 17 24 18.3 24 20V26C24 27.7 25.3 29 27 29H36L42 33V20Z"
                  stroke="url(#chat-gradient)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
                {/* Left bubble dots */}
                <Circle cx="11" cy="19" r="1.5" fill="url(#chat-gradient)" />
                <Circle cx="15" cy="19" r="1.5" fill="url(#chat-gradient)" />
                <Circle cx="19" cy="19" r="1.5" fill="url(#chat-gradient)" />
                {/* Right bubble dots */}
                <Circle cx="29" cy="23" r="1.5" fill="url(#chat-gradient)" />
                <Circle cx="33" cy="23" r="1.5" fill="url(#chat-gradient)" />
                <Circle cx="37" cy="23" r="1.5" fill="url(#chat-gradient)" />
              </Svg>
            </View>

            <Text
              accessibilityRole="header"
              accessibilityLevel={3}
              style={{
                fontSize: '20.8px',
                fontWeight: '600',
                marginBottom: 12,
                marginTop: 0,
                color: '#e2e8f0',
                lineHeight: '1.6',
                display: 'block',
                textAlign: 'center',
              }}
            >
              Connect Your Apps
            </Text>
            <Text
              style={{
                fontSize: '15.2px',
                color: '#94a3b8',
                lineHeight: '1.5',
                margin: 0,
                display: 'block',
                textAlign: 'center',
              }}
            >
              Choose the apps you text on{'\n'}
              <Text style={{ opacity: 0.8 }}>iMessage, WhatsApp, Signal & Telegram</Text>
            </Text>
          </View>

          <View
            className="step-card scroll-reveal"
            ref={(el: HTMLElement | null) => observe(el)}
            style={{
              background:
                'linear-gradient(135deg, rgba(26, 15, 46, 0.5) 0%, rgba(30, 20, 50, 0.6) 100%)',
              borderRadius: '1.5rem',
              padding: '2rem',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(167, 139, 250, 0.2)',
              position: 'relative',
              textAlign: 'center',
              flexGrow: 1,
              maxWidth: 365,
            }}
          >
            <View
              className="step-number"
              style={{
                width: 60,
                height: 60,
                borderRadius: 30,
                background: 'linear-gradient(135deg, #a78bfa 0%, #f9a8d4 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: 'auto',
                marginRight: 'auto',
                marginBottom: 24,
                fontSize: 24,
                fontWeight: 'bold',
                color: '#1a0f2e',
              }}
            >
              3
            </View>

            <View
              className="step-icon-container"
              style={{
                width: 140,
                height: 140,
                marginLeft: 'auto',
                marginRight: 'auto',
                marginBottom: 24,
                padding: 16,
                backgroundColor: 'rgba(167, 139, 250, 0.1)',
                borderRadius: 16,
                borderWidth: 1,
                borderColor: 'rgba(167, 139, 250, 0.3)',
                borderStyle: 'solid',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Svg viewBox="0 0 48 48" fill="none" style={{ width: '100%', height: '100%' }}>
                <Defs>
                  <LinearGradient id="insights-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <Stop offset="0%" stopColor="#a78bfa" />
                    <Stop offset="100%" stopColor="#f9a8d4" />
                  </LinearGradient>
                  <LinearGradient id="insights-fill" x1="0%" y1="0%" x2="100%" y2="100%">
                    <Stop offset="0%" stopColor="#a78bfa" stopOpacity="0.1" />
                    <Stop offset="100%" stopColor="#f9a8d4" stopOpacity="0.1" />
                  </LinearGradient>
                </Defs>
                <Circle cx="24" cy="21" r="8" fill="url(#insights-fill)" />
                <Circle
                  cx="24"
                  cy="21"
                  r="8"
                  stroke="url(#insights-gradient)"
                  strokeWidth="2"
                  fill="none"
                />
                <Path
                  d="M24 29C24 29 15 31 15 37L33 37C33 31 24 29 24 29Z"
                  fill="url(#insights-fill)"
                />
                <Path
                  d="M24 29C24 29 15 31 15 37L33 37C33 31 24 29 24 29Z"
                  stroke="url(#insights-gradient)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
                <Circle cx="21" cy="19" r="1.5" fill="url(#insights-gradient)" />
                <Circle cx="27" cy="19" r="1.5" fill="url(#insights-gradient)" />
                <Path
                  d="M20 23C20 23 21.5 25 24 25C26.5 25 28 23 28 23"
                  stroke="url(#insights-gradient)"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <PulsingCircle
                  cx={10}
                  cy={11}
                  minR={2}
                  maxR={2}
                  duration={2000}
                  delay={0}
                  fill="url(#insights-gradient)"
                  initialOpacity={0.6}
                />
                <PulsingCircle
                  cx={38}
                  cy={11}
                  minR={2}
                  maxR={2}
                  duration={2000}
                  delay={500}
                  fill="url(#insights-gradient)"
                  initialOpacity={0.6}
                />
                <PulsingCircle
                  cx={38}
                  cy={31}
                  minR={2}
                  maxR={2}
                  duration={2000}
                  delay={1000}
                  fill="url(#insights-gradient)"
                  initialOpacity={0.6}
                />
                <PulsingCircle
                  cx={10}
                  cy={31}
                  minR={2}
                  maxR={2}
                  duration={2000}
                  delay={1500}
                  fill="url(#insights-gradient)"
                  initialOpacity={0.6}
                />
              </Svg>
            </View>

            <Text
              accessibilityRole="header"
              accessibilityLevel={3}
              style={{
                fontSize: '20.8px',
                fontWeight: '600',
                marginBottom: 12,
                marginTop: 0,
                color: '#e2e8f0',
                lineHeight: '1.6',
                display: 'block',
                textAlign: 'center',
              }}
            >
              Meet Yourself
            </Text>
            <Text
              style={{
                fontSize: '15.2px',
                color: '#94a3b8',
                lineHeight: '1.5',
                margin: 0,
                display: 'block',
                textAlign: 'center',
              }}
            >
              Ask anything about yourself{'\n'}
              <Text style={{ opacity: 0.8 }}>and explore your results</Text>
            </Text>
          </View>
        </View>

        <View
          className="security-badges"
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 48,
            gap: '2rem',
          }}
        >
          <View
            className="security-badge"
            style={{
              display: 'inline-flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem 1.5rem',
              backgroundColor: 'rgba(26, 15, 46, 0.4)',
              borderStyle: 'solid',
              borderWidth: 1,
              borderColor: 'rgba(103, 232, 249, 0.2)',
              borderRadius: 16,
            }}
          >
            <Svg viewBox="0 0 20 20" fill="none" style={{ width: 20, height: 20, flexShrink: 0 }}>
              <Path
                d="M10 2L3 5V10C3 14.5 6 18.16 10 19C14 18.16 17 14.5 17 10V5L10 2Z"
                stroke="#67e8f9"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <Path
                d="M7 10L9 12L13 8"
                stroke="#67e8f9"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Svg>
            <View>
              <Text
                style={{ lineHeight: '1.6', fontSize: 14, fontWeight: '600', color: '#e2e8f0' }}
              >
                Privacy First
              </Text>
              <Text style={{ lineHeight: '1.6', fontSize: 12, color: '#94a3b8' }}>
                Never sold to anyone
              </Text>
            </View>
          </View>

          <View
            className="security-badge"
            style={{
              display: 'inline-flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem 1.5rem',
              backgroundColor: 'rgba(26, 15, 46, 0.4)',
              borderStyle: 'solid',
              borderWidth: 1,
              borderColor: 'rgba(249, 168, 212, 0.2)',
              borderRadius: 16,
            }}
          >
            <Svg viewBox="0 0 20 20" fill="none" style={{ width: 20, height: 20, flexShrink: 0 }}>
              <Rect x="4" y="6" width="12" height="10" rx="2" stroke="#f9a8d4" strokeWidth="1.5" />
              <Path
                d="M8 6V5C8 3.9 8.9 3 10 3C11.1 3 12 3.9 12 5V6"
                stroke="#f9a8d4"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <Circle cx="10" cy="11" r="1" fill="#f9a8d4" />
            </Svg>
            <View>
              <Text
                style={{ lineHeight: '1.6', fontSize: 14, fontWeight: '600', color: '#e2e8f0' }}
              >
                You’re in Control
              </Text>
              <Text style={{ lineHeight: '1.6', fontSize: 12, color: '#94a3b8' }}>
                Disconnect anytime
              </Text>
            </View>
          </View>

          <View
            className="security-badge"
            style={{
              display: 'inline-flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem 1.5rem',
              backgroundColor: 'rgba(26, 15, 46, 0.4)',
              borderStyle: 'solid',
              borderWidth: 1,
              borderColor: 'rgba(167, 139, 250, 0.2)',
              borderRadius: 16,
            }}
          >
            <Svg viewBox="0 0 20 20" fill="none" style={{ width: 20, height: 20, flexShrink: 0 }}>
              <Path
                d="M10 10C12.2091 10 14 8.20914 14 6C14 3.79086 12.2091 2 10 2C7.79086 2 6 3.79086 6 6C6 8.20914 7.79086 10 10 10Z"
                stroke="#a78bfa"
                strokeWidth="1.5"
              />
              <Path
                d="M10 12C6 12 3 14 3 17V18H17V17C17 14 14 12 10 12Z"
                stroke="#a78bfa"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </Svg>
            <View>
              <Text
                style={{ lineHeight: '1.6', fontSize: 14, fontWeight: '600', color: '#e2e8f0' }}
              >
                Delete on Request
              </Text>
              <Text style={{ lineHeight: '1.6', fontSize: 12, color: '#94a3b8' }}>
                Just ask, anytime
              </Text>
            </View>
          </View>
        </View>
      </View>

      {/* Testimonials */}
      <View className="testimonials-section">
        <Text className="section-title" accessibilityRole="header" accessibilityLevel={2}>
          Sound Like Anyone You Know?
        </Text>
        <View className="testimonials-grid">
          {[
            { line: archetypeLine('sex-and-the-city', 'miranda'), to: 'a Miranda' },
            { line: archetypeLine('hogwarts', 'gryffindor'), to: 'a Gryffindor' },
            { line: myersBriggsTypes.INFJ.line, to: 'an INFJ' },
          ].map((reading) => (
            <View
              key={reading.to}
              className="testimonial-card scroll-reveal"
              ref={(el: HTMLElement | null) => observe(el)}
            >
              <Text className="testimonial-quote">“{reading.line}”</Text>
              <Text className="testimonial-author">— SoulThread, to {reading.to}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* FAQ */}
      <View className="faq-section">
        <Text className="section-title" accessibilityRole="header" accessibilityLevel={2}>
          Frequently Asked Questions
        </Text>
        <View className="faq-list">
          {[
            {
              q: 'Is my data safe?',
              a: 'We never sell your personal information, and your results are shown only to you. You can disconnect any app or ask us to delete your data at any time.',
            },
            {
              q: 'What messaging apps are supported?',
              a: 'iMessage, WhatsApp, Signal, and Telegram. Connect them once in the Mac app and they stay in sync on their own while your Mac is on, there and on the web.',
            },
            {
              q: 'What can I ask it?',
              a: 'Anything about yourself: how you come across, who you’re closest to, or what changed with an old friend. Every answer comes from your own messages.',
            },
            {
              q: 'How accurate is the analysis?',
              a: 'SoulThread looks for patterns across the apps you connect. Its insights are reflections, not verdicts, and when you ask it something it shows the messages behind its answer so you can judge for yourself.',
            },
          ].map((item, i) => (
            <details key={i} className="faq-item">
              <summary className="faq-question">{item.q}</summary>
              <div className="faq-answer">{item.a}</div>
            </details>
          ))}
        </View>
      </View>

      <View className="cta-section">
        <Text
          className="cta-title"
          accessibilityRole="header"
          accessibilityLevel={2}
          style={{ fontWeight: 'bold' }}
        >
          Where will your SoulThread take you?
        </Text>
        <Text className="cta-subtitle">
          {waitlistMode
            ? 'Get early access when SoulThread launches.'
            : 'Explore the patterns in your everyday conversations.'}
        </Text>
        {waitlistMode ? (
          <WaitlistForm />
        ) : (
          <OpenAppLink className="btn btn-primary btn-large">
            Open SoulThread <span aria-hidden="true">↗</span>
          </OpenAppLink>
        )}
      </View>
    </View>
  );
};

// Privacy Policy Page
const PrivacyPage = () => (
  <div className="legal-page">
    <Link to="/" className="back-to-home">
      &larr; Back to Home
    </Link>
    <h1>Privacy Policy</h1>
    <p className="legal-meta">Last updated: September 28, 2026</p>
    <div className="legal-content">
      <section>
        <h2>Overview</h2>
        <p>
          This Privacy Policy explains how SoulThread (&quot;SoulThread,&quot; &quot;we,&quot;
          &quot;us,&quot; or &quot;our&quot;) collects, uses, and shares information when you use
          our website at soulthread.me and related products and services (the &quot;Services&quot;).
        </p>
        <p>
          By using the Services, you agree to this Privacy Policy. If you do not agree, please do
          not use the Services.
        </p>
      </section>

      <section>
        <h2>Information We Collect</h2>
        <p>Depending on how you use SoulThread, we may collect:</p>
        <ul>
          <li>Contact details such as your email address (for waitlist and product updates).</li>
          <li>Account/authentication data such as phone number and verification metadata.</li>
          <li>
            Content you choose to provide, including imported messages, quiz responses, and profile
            details.
          </li>
          <li>
            Technical information such as device/browser details, approximate location from IP, and
            usage logs.
          </li>
          <li>
            Support and communication records when you contact us by email or other support
            channels.
          </li>
          <li>
            A record of any purchase, such as its amount and date. Stripe processes payments; we
            never receive your card details.
          </li>
          <li>
            If you connect WhatsApp, Signal or Telegram on the web: the linked session, kept
            encrypted on SoulThread&apos;s servers until you disconnect or delete your account, and
            a record of the Terms you agreed to when you connected it.
          </li>
        </ul>
      </section>

      <section>
        <h2>How We Use Information</h2>
        <p>We use information to:</p>
        <ul>
          <li>Operate, maintain, and improve the Services.</li>
          <li>Provide personality insights and related product features.</li>
          <li>Authenticate users and protect account security.</li>
          <li>Send service messages, launch updates, and support responses.</li>
          <li>Monitor performance, prevent abuse, and troubleshoot errors.</li>
          <li>Comply with legal obligations and enforce our Terms.</li>
        </ul>
      </section>

      <section>
        <h2>Legal Bases (Where Applicable)</h2>
        <p>Where required by law, we process personal data based on one or more of:</p>
        <ul>
          <li>Your consent.</li>
          <li>Performance of a contract with you.</li>
          <li>Our legitimate interests in operating and securing the Services.</li>
          <li>Compliance with legal obligations.</li>
        </ul>
      </section>

      <section>
        <h2>How We Share Information</h2>
        <p>We do not sell your personal information. We may share information:</p>
        <ul>
          <li>With service providers that help us host, analyze, and operate the Services.</li>
          <li>With Stripe, to process a payment you choose to make.</li>
          <li>With infrastructure providers for storage, security, and delivery.</li>
          <li>If required by law, legal process, or to protect rights and safety.</li>
          <li>
            In connection with a merger, acquisition, financing, or sale of all or part of our
            business.
          </li>
        </ul>
      </section>

      <section>
        <h2>Data Retention</h2>
        <p>
          We retain personal information for as long as needed to provide the Services, comply with
          legal obligations, resolve disputes, and enforce agreements. Retention periods vary by
          data type and use case.
        </p>
      </section>

      <section>
        <h2>Data Security</h2>
        <p>
          We use reasonable administrative, technical, and organizational safeguards to protect
          personal information. No method of transmission or storage is completely secure, and we
          cannot guarantee absolute security.
        </p>
      </section>

      <section>
        <h2>Your Choices and Rights</h2>
        <ul>
          <li>
            You can request access, correction, export, or deletion of your information, subject to
            applicable law.
          </li>
          <li>You can unsubscribe from product update emails at any time.</li>
          <li>You can contact us to ask questions about your privacy choices.</li>
        </ul>
      </section>

      <section>
        <h2>International Users</h2>
        <p>
          If you use the Services outside the country where our systems are located, your
          information may be processed in other countries that may have different data protection
          laws.
        </p>
      </section>

      <section>
        <h2>Children&apos;s Privacy</h2>
        <p>
          The Services are not directed to children under 13, and we do not knowingly collect
          personal information from children under 13.
        </p>
      </section>

      <section>
        <h2>Policy Updates</h2>
        <p>
          We may update this Privacy Policy from time to time. When we do, we will post the updated
          version and revise the &quot;Last updated&quot; date above.
        </p>
      </section>

      <section>
        <h2>Contact</h2>
        <p>
          For privacy questions or requests, contact us at{' '}
          <a href="mailto:privacy@soulthread.me">privacy@soulthread.me</a>
        </p>
      </section>
    </div>
  </div>
);

// Terms Page
const TermsPage = () => (
  <div className="legal-page">
    <Link to="/" className="back-to-home">
      &larr; Back to Home
    </Link>
    <h1>Terms & Conditions</h1>
    <p className="legal-meta">Last updated: September 28, 2026</p>
    <div className="legal-content">
      <section>
        <h2>Acceptance of Terms</h2>
        <p>
          These Terms & Conditions (&quot;Terms&quot;) govern your access to and use of the Services
          provided by SoulThread. By accessing or using the Services, you agree to these Terms. If
          you do not agree, do not use the Services.
        </p>
      </section>

      <section>
        <h2>Eligibility and Accounts</h2>
        <ul>
          <li>You must be legally able to enter into this agreement.</li>
          <li>You are responsible for information submitted through your account.</li>
          <li>You are responsible for safeguarding your account credentials and access methods.</li>
        </ul>
      </section>

      <section>
        <h2>Permitted Use</h2>
        <p>
          You may use the Services only in compliance with applicable law and these Terms. You agree
          not to:
        </p>
        <ul>
          <li>Use the Services for unlawful, deceptive, abusive, or harmful purposes.</li>
          <li>Interfere with service availability, security, or integrity.</li>
          <li>Attempt unauthorized access to accounts, systems, or data.</li>
          <li>Reverse engineer, scrape, or extract source code except where permitted by law.</li>
          <li>Use third-party data without appropriate rights or permissions.</li>
        </ul>
      </section>

      <section>
        <h2>User Content</h2>
        <p>
          You retain rights to content you provide. You grant SoulThread a limited license to host,
          process, and display your content as needed to provide and improve the Services.
        </p>
      </section>

      <section>
        <h2>Messengers Connected on the Web</h2>
        <p>
          When you continue with WhatsApp, Signal or Telegram on soulthread.me, you link your
          account to SoulThread as you would a computer. SoulThread holds that link on its own
          servers, encrypted, to bring in your conversations and keep them in sync without the Mac
          app. It is used for nothing else: SoulThread sends no messages from it except the one-time
          code it puts in your chat with yourself to confirm the sign-in.
        </p>
        <p>
          SoulThread connects to WhatsApp the way WhatsApp on a computer does, but it is not made or
          endorsed by WhatsApp, and WhatsApp may limit or suspend accounts that use apps it
          didn&apos;t make. You accept that risk when you continue with WhatsApp.
        </p>
        <p>
          SoulThread connects to Signal as a linked device with signal-cli, open-source software
          that Signal doesn&apos;t make or endorse. Signal gives a newly linked device only the
          messages that arrive after it is linked, so SoulThread brings in your Signal conversations
          from then on.
        </p>
        <p>
          You can disconnect anytime in Settings, or remove SoulThread from your messenger&apos;s
          list of linked devices; either way SoulThread logs the link out and deletes it. Deleting
          your account does the same. Your use of each messenger stays subject to its own terms.
        </p>
      </section>

      <section>
        <h2>Purchases</h2>
        <p>
          Your first few results are free. Unlocking every other result is a one-time payment,
          processed by Stripe; SoulThread never sees your card details. Those results are written
          for you after you pay. If something isn&apos;t right, contact us and we&apos;ll refund
          you. A refund locks those results again.
        </p>
      </section>

      <section>
        <h2>Results Are for Reflection</h2>
        <p>
          Results are playful readings of how you write, made with AI. They are not clinical,
          psychological, or professional assessments. Comparisons with characters from shows, books,
          or music are unofficial and imply no affiliation or endorsement.
        </p>
      </section>

      <section>
        <h2>Intellectual Property</h2>
        <p>
          The Services, including software, branding, design, and related materials, are owned by
          SoulThread or its licensors and are protected by applicable intellectual property laws.
        </p>
      </section>

      <section>
        <h2>Service Changes and Availability</h2>
        <p>
          We may modify, suspend, or discontinue features at any time, with or without notice. We do
          not guarantee that the Services will be uninterrupted, secure, or error-free.
        </p>
      </section>

      <section>
        <h2>Disclaimer of Warranties</h2>
        <p>
          The Services are provided &quot;as is&quot; and &quot;as available&quot; without
          warranties of any kind, express or implied, to the extent permitted by law.
        </p>
      </section>

      <section>
        <h2>Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by law, SoulThread and its affiliates will not be liable
          for indirect, incidental, special, consequential, exemplary, or punitive damages, or for
          any loss of data, profits, revenues, or goodwill.
        </p>
      </section>

      <section>
        <h2>Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless SoulThread, its affiliates, and personnel from
          claims and expenses arising out of your use of the Services or violation of these Terms.
        </p>
      </section>

      <section>
        <h2>Termination</h2>
        <p>
          We may suspend or terminate your access if you violate these Terms or create legal,
          security, or operational risk. You may stop using the Services at any time.
        </p>
      </section>

      <section>
        <h2>Governing Law</h2>
        <p>
          These Terms are governed by applicable laws of the jurisdiction in which SoulThread
          operates, without regard to conflict-of-law principles.
        </p>
      </section>

      <section>
        <h2>Changes to Terms</h2>
        <p>
          We may update these Terms from time to time. When we do, we will post the updated version
          and revise the &quot;Last updated&quot; date above. Continued use of the Services after
          updates means you accept the revised Terms.
        </p>
      </section>

      <section>
        <h2>Contact</h2>
        <p>
          For questions about these terms, contact us at{' '}
          <a href="mailto:legal@soulthread.me">legal@soulthread.me</a>
        </p>
      </section>
    </div>
  </div>
);

// Contact Page
const ContactPage = () => (
  <div className="legal-page contact-page">
    <h1>Contact Us</h1>
    <div className="contact-content">
      <p>We'd love to hear from you! Reach out through any of the following channels:</p>

      <div className="contact-methods">
        <div className="contact-method">
          <h3>General Inquiries</h3>
          <p>
            <a href="mailto:kindred@soulthread.me">kindred@soulthread.me</a>
          </p>
        </div>
        <div className="contact-method">
          <h3>Support</h3>
          <p>
            <a href="mailto:support@soulthread.me">support@soulthread.me</a>
          </p>
        </div>
        <div className="contact-method">
          <h3>Bug Reports</h3>
          <p>
            Report issues to <a href="mailto:support@soulthread.me">support@soulthread.me</a>
          </p>
        </div>
      </div>
    </div>
  </div>
);

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [detectedPlatform, setDetectedPlatform] = React.useState<
    'ios' | 'android' | 'mac' | 'windows' | 'linux' | 'unknown'
  >('unknown');
  const [showNavbar, setShowNavbar] = React.useState(false);
  const [showScrollTop, setShowScrollTop] = React.useState(false);
  const waitlistModeOverride = import.meta.env.VITE_WAITLIST_MODE;
  const waitlistMode = waitlistModeOverride === 'true';

  React.useEffect(() => {
    // Detect OS and device type
    const userAgent = navigator.userAgent.toLowerCase();
    const platform = navigator.platform.toLowerCase();

    // Detect specific platform
    if (/iphone|ipad|ipod/i.test(userAgent)) {
      setDetectedPlatform('ios');
    } else if (/android/i.test(userAgent)) {
      setDetectedPlatform('android');
    } else if (platform.includes('mac') || /macintosh|mac os x/i.test(userAgent)) {
      setDetectedPlatform('mac');
    } else if (platform.includes('win') || /windows|win32/i.test(userAgent)) {
      setDetectedPlatform('windows');
    } else if (platform.includes('linux') || /linux/i.test(userAgent)) {
      setDetectedPlatform('linux');
    }

    // Smooth scroll to hash
    if (window.location.hash === '#app-downloads') {
      const element = document.querySelector('.app-downloads');
      element?.scrollIntoView({ behavior: 'smooth' });
    } else if (window.location.hash === '#waitlist') {
      const waitlistElement = document.getElementById('waitlist');
      waitlistElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const scrollToWaitlist = React.useCallback(() => {
    const waitlistElement = document.getElementById('waitlist');
    if (!waitlistElement) return false;
    waitlistElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  }, []);

  const handleWaitlistNavClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      if (location.pathname !== '/') {
        navigate('/#waitlist');
        setTimeout(() => {
          scrollToWaitlist();
        }, 120);
        return;
      }

      scrollToWaitlist();
    },
    [location.pathname, navigate, scrollToWaitlist]
  );

  React.useEffect(() => {
    const handleScroll = () => {
      const heroContent = document.querySelector('.hero-content');
      if (heroContent) {
        const rect = heroContent.getBoundingClientRect();
        const heroHeight = rect.height;
        const heroTop = rect.top;
        // Show navbar when the top of the screen is past the halfway point of hero-content
        const shouldShow = heroTop < -(heroHeight / 2);
        setShowNavbar(shouldShow);
      }
      setShowScrollTop(window.scrollY > 600);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (location.pathname === '/sync') return <Navigate to="/app/devices" replace />;
  if (location.pathname === '/app' || location.pathname.startsWith('/app/'))
    return <RealtimePage />;
  if (location.pathname === '/admin') return <AdminPage />;

  return (
    <View className="app">
      <View className={`navbar ${showNavbar ? 'navbar-visible' : 'navbar-hidden'}`}>
        <NavBarLogo />
        <View className="nav-links">
          {waitlistMode ? (
            <>
              <OpenAppLink className="nav-open-app">Open app ↗</OpenAppLink>
              <a
                href="#waitlist"
                onClick={handleWaitlistNavClick}
                className="btn btn-primary btn-small nav-download-btn"
              >
                Join Waitlist
              </a>
            </>
          ) : (
            <>
              {/* The web app is the way in; the Mac app is where apps get connected. */}
              {detectedPlatform === 'mac' && (
                <Link to="/download" className="nav-open-app">
                  Mac app
                </Link>
              )}
              <OpenAppLink className="btn btn-primary btn-small nav-download-btn">
                Open app ↗
              </OpenAppLink>
            </>
          )}
        </View>
      </View>

      <View className="main-content">
        <PageTransition>
          <Routes>
            <Route path="/" element={<HomePage waitlistMode={waitlistMode} />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/download" element={<DownloadPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </PageTransition>
      </View>

      <button
        className={`scroll-to-top ${showScrollTop ? 'visible' : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Scroll to top"
      >
        <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
          <Path
            d="M18 15l-6-6-6 6"
            stroke="#a78bfa"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      </button>

      <View className="footer">
        <View className="footer-content">
          <View className="footer-copyright-container">
            <Text className="footer-copyright">
              © {new Date().getFullYear()} SoulThread. All rights reserved.
            </Text>
          </View>
          <View className="footer-links">
            <OpenAppLink>Open web app</OpenAppLink>
            <Text className="footer-separator">•</Text>
            <Link to="/privacy">Privacy</Link>
            <Text className="footer-separator">•</Text>
            <Link to="/terms">Terms</Link>
            <Text className="footer-separator">•</Text>
            <Link to="/contact">Contact</Link>
          </View>
        </View>
      </View>
    </View>
  );
}

export default App;
